import React, { useEffect } from 'react'
import { useFlag } from 'constants/flags'
import {
  getPageLoadMetrics,
  PageLoadPayload,
} from '../pageLoad/getPageLoadMetrics'
import { trackMetric } from '../telemetry'

import type { TelemetryMetric, TelemetryMetricId } from '../telemetry/types'

const initializePageLoadAnalytics = () => {
  // expose global metrics object for local debugging
  let metrics: PageLoadPayload | null = null
  ;(window as any).__getPageLoadMetrics = () => metrics

  const createTelemetryPayload = (
    id: TelemetryMetricId,
    value: number = 0,
    route: string
  ): TelemetryMetric => {
    const valueInSeconds = (value ?? 0) / 1000 // convert to seconds
    const truncatedValue = Math.round(valueInSeconds * 100) / 100 // truncate to 2 decimal places

    return {
      id,
      value: truncatedValue,
      labels: {
        route,
      },
    }
  }

  getPageLoadMetrics((data, name) => {
    // update metrics object
    metrics = data

    const route = data.route.template!

    if (name === 'lcp') {
      trackMetric(
        createTelemetryPayload(
          'webapp_lcp_raw_duration_seconds',
          data.lcp?.metric.value,
          route
        )
      )

      if (data.lcp?.isAfterReceivingData) {
        trackMetric(
          createTelemetryPayload(
            'webapp_lcp_filtered_duration_seconds',
            data.lcp?.metric.value,
            route
          )
        )
      }
    }

    if (name === 'elementTiming') {
      trackMetric(
        createTelemetryPayload(
          'webapp_element_timing_duration_seconds',
          data.elementTiming?.value,
          route
        )
      )
    }

    if (name === 'customElementTiming') {
      trackMetric(
        createTelemetryPayload(
          'webapp_custom_element_timing_duration_seconds',
          data.customElementTiming?.value,
          route
        )
      )
    }
  }, window.location.pathname)
}

export const PageLoadAnalytics: React.FC = () => {
  // Using a feature flag means we'll only be able to collect
  // metrics from authenticated users.
  // Flag removal ticket:
  //  - https://github.com/sketch-hq/Cloud/issues/9819
  //  - https://linear.app/sketch/issue/CLC-2525
  const sendMetrics = useFlag('page-load-metrics')

  useEffect(() => {
    // We want to avoid analytics requests if runnning under Cypress
    if ((window as any).Cypress) {
      return
    }

    if (sendMetrics) {
      initializePageLoadAnalytics()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return null
}
